import type { FileLifecycleState } from "./fileLifecycle";

/**
 * cloudSaveGate — may the editor's CURRENT scene be written to the open cloud
 * file yet?
 *
 * Opening `/f/<id>` mounts the editor on whatever scene it already had (the
 * anonymous scratch canvas — usually empty) and only THEN fetches the file and
 * hydrates it. That initial scene fires an `onChange`, which armed the autosave
 * debounce. If hydration lost the race with the debounce — a slow network, or
 * simply the auth session resolving late, which re-runs the loader — the editor
 * flushed the empty pre-hydration scene straight over the file and destroyed
 * it. (Reproduced in the browser: a saved rectangle came back as an empty
 * canvas, "Saved · just now".)
 *
 * The invariant: never write a scene to a file whose scene we have not loaded.
 * An `open` file must be hydrated first; a file being created here (`pendingNew`
 * / `creating`) has nothing to hydrate — the scene in the editor IS the file.
 */
export const isSafeToSaveCloudScene = ({
  lifecycleKind,
  isHydrated,
}: {
  lifecycleKind: FileLifecycleState["kind"];
  /** The open file's scene (or its offline buffer) has been applied to the editor. */
  isHydrated: boolean;
}): boolean => (lifecycleKind === "open" ? isHydrated : true);
